/**
 * vitalAdvisory — ADVISORY, NON-DIAGNOSTIC out-of-range flagging for vitals (P2). PURE + data-driven.
 *
 * Reuses deriveVitalType so we ONLY ever flag a measure whose TYPE is CONFIDENTLY known from the row's
 * real columns. This is an ADVISORY scannability aid using standard ADULT reference ranges — it is NEVER a
 * diagnostic or clinical determination, it changes NO data / count / provenance, and it deliberately does
 * NOT flag:
 *   - unstructured / free-text vitals (no numeric value to compare, never trended);
 *   - "coded" vitals whose type op_* could not resolve (we do not guess what they measure);
 *   - "%" / percentage (the codebase refuses to assume a % is SpO2 — never guessed);
 *   - rates in "/min" (ambiguous heart vs respiratory rate); only an explicit "bpm" beat-rate is flagged;
 *   - weight / height / BMI / generic pressure (no single adult advisory cutoff is carried here).
 *
 * Flagged kinds + adult advisory ranges (out-of-range => return a short reason):
 *   - bp          : systolic >= 140 or < 90, OR diastolic >= 90 or < 60
 *   - rate (bpm)  : heart rate < 60 or > 100
 *   - temperature : >= 38.0 C (>= 100.4 F)  [unit-aware; C vs F resolved from value_unit]
 *
 * Returns a short reason string when CONFIDENTLY-typed AND out of range; otherwise null (which covers an
 * in-range value, an unstructured/free-text row, an ambiguous type, and a missing numeric value). The
 * caller renders a subtle advisory marker only — never a clinical verdict.
 */
import { deriveVitalType, type VitalTypeInput } from "./vitalType";

/** Minimum-necessary row shape: the deriveVitalType inputs plus the numeric value it compares. */
export interface VitalAdvisoryInput extends VitalTypeInput {
  value_quantity?: number | null;
}

/** The clear, reused "advisory, not diagnostic" disclaimer shown beside any advisory marker. */
export const VITAL_ADVISORY_LEGEND =
  "Advisory, not diagnostic: out-of-range marks use standard adult reference ranges as a scannability " +
  "aid only. They are not a clinical determination and do not change any value, count, or provenance.";

/** The short label rendered on a flagged tile/cell (kept constant so it never reads as a diagnosis). */
export const VITAL_ADVISORY_LABEL = "Out of range (advisory)";

export function vitalAdvisory(row: VitalAdvisoryInput): string | null {
  const info = deriveVitalType(row);
  const unit = row.value_unit?.trim().toLowerCase();
  switch (info.kind) {
    case "bp": {
      const sys = row.component_systolic;
      const dia = row.component_diastolic;
      const sysFlag = sys != null && (sys >= 140 || sys < 90);
      const diaFlag = dia != null && (dia >= 90 || dia < 60);
      if (sysFlag || diaFlag) return "Advisory: BP outside the 90/60 to 140/90 adult range";
      return null;
    }
    case "rate": {
      // Only an explicit beats-per-minute rate is CONFIDENTLY a heart rate; "/min" is ambiguous
      // (could be respiratory rate), so we never flag it.
      if (unit !== "bpm") return null;
      const v = row.value_quantity;
      if (v != null && (v < 60 || v > 100)) return "Advisory: heart rate outside the 60 to 100 bpm adult range";
      return null;
    }
    case "temperature": {
      const v = row.value_quantity;
      if (v == null) return null;
      const isF = unit === "f" || unit === "°f" || unit === "degf";
      const high = isF ? 100.4 : 38.0;
      if (v >= high) return `Advisory: temperature at/above ${isF ? "100.4 F" : "38.0 C"}`;
      return null;
    }
    default:
      return null;
  }
}
